import ApiResponseV2 from '../core/ApiResponseV2';
import { AuthenticationToken } from '../core/AuthenticationToken';
import GenericResponse from '../core/GenericResponse';
import PublicAuthenticationToken from '../core/PublicAuthenticationToken';
import User from '../core/User';
import httpClientV2 from '../utils/HttpClientV2';
import NotificationService from './NotificationService';

/**
 * CustomAuthServiceV2 is a class that provides methods to interact with a generic authentication API.
 */
class CustomAuthServiceV2 {
  /**
   * Retrieves the login method available for a given email.
   *
   * @param email - The email to retrieve the login method for.
   * @returns A Promise that resolves to an ApiResponseV2 containing the login method as a string.
   */

  getLoginMethod(email: string): Promise<ApiResponseV2<string>> {
    return httpClientV2.get(`/auth/login-method?email=${email}`);
  }

  /**
   * Validates the login using the specified access URL, email and access key.
   *
   * @param accessUrl - The access URL to use for the login validation.
   * @param email - The email to use for the login validation.
   * @param accessKey - The access key to use for the login validation.
   * @returns A Promise that resolves to an ApiResponseV2 containing the authentication token as a PublicAuthenticationToken object.
   */

  validateLogin(accessUrl: string, email: string, accessKey: string): Promise<ApiResponseV2<PublicAuthenticationToken>> {
    return httpClientV2.post(`/auth${accessUrl}`, {
      email,
      password: accessKey,
    });
  }

  /**
   * Validates the login using the specified email and password.
   *
   * @param email - The email to use for the login validation.
   * @param password - The password to use for the login validation.
   * @returns A Promise that resolves to an ApiResponseV2 containing the authentication token as a PublicAuthenticationToken object.
   */
  validateLoginWithEmailAndPassword(email: string, password: string): Promise<ApiResponseV2<PublicAuthenticationToken>> {
    return this.validateLogin('/login', email, password);
  }

  /**
   * Validates the login using the specified email and code.
   *
   * @param email - The email to use for the login validation.
   * @param code - The code to use for the login validation.
   * @returns A Promise that contains a response depending on if the code is valid or not.
   */
  validateLoginWithCode(email: string, code: string): Promise<ApiResponseV2<AuthenticationToken>> {
    return httpClientV2.post(`/auth/login-with-code`, {
      email,
      code,
    });
  }

  /**
   * Sends a login code to the specified email.
   *
   * @param email - The email to send the login code to.
   * @returns A Promise that resolves to the response sent by the server
   */
  startLoginWithCode(email: string) {
    return httpClientV2.post(`/auth/login-code`, {
      email,
    });
  }

  /**
   * Registers a new user with the specified details.
   *
   * @param firstName - The first name of the user to be registered.
   * @param lastName - The last name of the user to be registered.
   * @param password - The password of the user to be registered.
   * @param passwordConfirmation - The password confirmation of the user to be registered.
   * @param email - The email of the user to be registered.
   * @returns A Promise that contains a response backend-implementation dependent, on if the registration was successful or not
   */
  register(
    firstName: string,
    lastName: string,
    password: string,
    passwordConfirmation: string,
    email: string
  ): Promise<ApiResponseV2<GenericResponse>> {
    return httpClientV2.post(`/auth/sign-up`, {
      firstName,
      lastName,
      password,
      passwordConfirmation,
      email,
    });
  }

  /**
   * Registers a user using a custom authentication path.
   *
   * @param firstName - The user's first name.
   * @param lastName - The user's last name.
   * @param password - The user's password.
   * @param passwordConfirmation - The user's password confirmation.
   * @param phone - The user's phone number.
   * @param email - The user's email address.
   * @param customAuthPath - A custom authentication path.
   * @returns - A Promise that contains a response that depends on the backend implementation
   */
  registerToCustomPath(
    firstName: string,
    lastName: string,
    password: string,
    passwordConfirmation: string,
    phone: string,
    email: string,
    customAuthPath: Function
  ): Promise<ApiResponseV2<GenericResponse>> {
    return httpClientV2.post(customAuthPath(), {
      firstName,
      lastName,
      password,
      passwordConfirmation,
      phone,
      email,
    });
  }

  /**
   * Registers a new user with the specified details, especially with tenant.
   *
   * @param firstName - The first name of the user to be registered.
   * @param lastName - The last name of the user to be registered.
   * @param password - The password of the user to be registered.
   * @param passwordConfirmation - The password confirmation of the user to be registered.
   * @param phone - The phone number of the user to be registered.
   * @param email - The email of the user to be registered.
   * @param tenantId - The tenant ID where you want to add the user.
   * @returns A Promise that contains a response backend-implementation dependent, on if the registration was successful or not
   */
  registerWithTenant(
    firstName: string,
    lastName: string,
    password: string,
    passwordConfirmation: string,
    phone: string,
    email: string,
    tenantId: string
  ): Promise<ApiResponseV2<GenericResponse>> {
    return httpClientV2.post(`/auth/sign-up-with-tenant`, {
      firstName,
      lastName,
      password,
      passwordConfirmation,
      phone,
      email,
      tenantId,
    });
  }

  /**
   * Requests a password reset for a user.
   *
   * @param email - The user's email address.
   *
   * @returns A Promise that resolves to an ApiResponseV2 object
   * that contains a user password reset link
   */
  forgotPassword(email: string) {
    return httpClientV2.post(`/auth/forgot-password`, {
      email,
    });
  }

  /**
   * Validates a password reset request.
   *
   * @param email - The user's email address.
   * @param validationCode - The validation code received from the password reset request.
   * @param password - The user's new password.
   */
  validateForgotPassword(email: string, validationCode: string, password: string) {
    return httpClientV2.post(`/auth/validate-forgot-password`, {
      email,
      validationCode,
      password,
    });
  }

  /**
   * Validates a user account using a validation code and email address.
   *
   * @param code The validation code received from the account validation request.
   * @param email The user's email address.
   * @returns A Promise, that depends on the backend implementation
   */
  validateAccount(code: string, email: string) {
    return httpClientV2.get(`/auth/validate?code=${code}&email=${email}`);
  }

  /**
   * Logs out the user from OAuth.
   */
  oAuthLogout() {
    return httpClientV2.post<{}, { result: 'ok' }>(`/oauth/logout`, {});
  }

  /**
   * Logs out the user from the current session.
   */
  logoutCurrentSession() {
    return httpClientV2.delete(`/auth/logout`);
  }

  /**
   * Updates a user's password.
   *
   * @param email The user's email address.
   * @param password The user's new password.
   * @returns A Promise that contains a response that depends on the backend implementation
   *
   * We also use the Notification service to notify any observers
   * of the updated password
   */
  async updatePassword(email: string, password: string): Promise<ApiResponseV2<GenericResponse>> {
    var response: ApiResponseV2<GenericResponse> = await httpClientV2.put(`/auth/update-password`, { email, password });

    response.onSuccess(() => NotificationService.info('Password updated successfully')).notifyErrorIfPresent();

    return response;
  }

  /**
   * Logs in a user using password-less authentication.
   *
   * @param email The user's email address.
   * @returns A Promise that contains a response that depends on the backend implementation
   */
  paswordLessLoginByEmail(email: string): Promise<ApiResponseV2<PublicAuthenticationToken>> {
    return httpClientV2.post(`auth/login-as/${email}`, {});
  }

  /**
   * Returns the user that is currently authenticated or returns an error otherwise
   * @returns The authenticated user
   */
  getAuthenticatedUser(): Promise<ApiResponseV2<User>> {
    return httpClientV2.get(`auth/current-user`);
  }
}

export default new CustomAuthServiceV2();
